import {useCompanyMembersQuery} from "@/api/companyApi";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import SearchIcon from "@/assets/colorable/search.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import AnimatedAccordion from "@/components/Common/animated/AnimatedAccordion";
import InputField from "@/components/Common/forms/InputField";
import ScopeAvatar from "@/components/Settings/ScopeAvatar";
import {usePaymentFailedInvoices} from "@/hooks/usePaymentFailedInvoices";
import {useSettingsScopeRows} from "@/hooks/useSettingsScopeRows";
import {useSettingsSectionItems} from "@/hooks/useSettingsSectionItems";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {AbilityContext} from "@/permissions";
import {INFINITY_THRESHOLD, SETTINGS_ACCOUNT_SECTIONS, SETTINGS_ADMIN_SECTIONS} from "@/store/constants";
import type {
  TSettingsScope,
  TSettingsScopeRow,
  TSettingsScopeType,
  TSettingsSection,
  TSettingsSubview,
} from "@/store/types";
import {Link} from "@tanstack/react-router";
import {ExternalLink} from "lucide-react";
import {useContext, useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce} from "react-use";

const SEARCH_THRESHOLD = 8;

const getScopeKey = ({id, type}: {id: number; type: TSettingsScopeType}) => `${type}-${id}`;

const SettingsRail: FC<{
  scope: TSettingsScope;
  onSelect(params: {scope?: TSettingsScope; section: TSettingsSection; subview?: TSettingsSubview}): void;
  section: TSettingsSection;
}> = ({scope, onSelect, section}) => {
  const {t} = useTranslation(["common", "company_profile", "multi_account", "settings"]);
  const isAdmin = useIsAdmin();
  const sectionItems = useSettingsSectionItems();
  const {getPaymentFailedInvoices} = usePaymentFailedInvoices();
  const navRef = useRef<HTMLElement>(null);
  const scopeKey = getScopeKey(scope);
  const [expandedKey, setExpandedKey] = useState<string | null>(scopeKey);
  const [renderedScopeKey, setRenderedScopeKey] = useState(scopeKey);
  const [brandQuery, setBrandQuery] = useState("");
  const [debouncedBrandQuery, setDebouncedBrandQuery] = useState("");
  const {agency, agencyRows, brandRows, flatRow, seatsLeft} = useSettingsScopeRows({scope});
  const canUpdateMembers = useContext(AbilityContext).can("update", "CompanyMembers");
  const {data: scopeMembers} = useCompanyMembersQuery({
    companyId: scope.type === "brand" ? scope.id : 0,
    queryOptions: {enabled: scope.type === "brand" && canUpdateMembers},
  });
  const hasPendingPermissionRequest =
    scopeMembers?.some(member => member.permission_request?.status === "PENDING") ?? false;

  useDebounce(() => setDebouncedBrandQuery(brandQuery), 300, [brandQuery]);

  if (renderedScopeKey !== scopeKey) {
    setRenderedScopeKey(scopeKey);
    setExpandedKey(scopeKey);
  }

  // The rail can open on a brand far down a long client list. The whole row is scrolled, not just its
  // header, so its expanded sections come along; the row counts are dependencies because the rows
  // arrive with their query - on open there is nothing to scroll to yet.
  useEffect(() => {
    navRef.current?.querySelector<HTMLElement>("[data-active-scope='true']")?.scrollIntoView({block: "nearest"});
  }, [agencyRows.length, brandRows.length, scopeKey]);

  const normalizedBrandQuery = debouncedBrandQuery.trim().toLowerCase();
  const visibleBrandRows = normalizedBrandQuery
    ? brandRows.filter(row => row.name.toLowerCase().includes(normalizedBrandQuery))
    : brandRows;

  const renderSectionItem = ({
    indicator,
    isActive,
    itemSection,
    onClick,
  }: {
    indicator?: {label: string; tone: "danger" | "warning"};
    isActive: boolean;
    itemSection: TSettingsSection;
    onClick(): void;
  }) => {
    const {icon: ItemIcon, label} = sectionItems[itemSection];

    return (
      <button
        key={itemSection}
        className={clsxm(
          "max-sm:bg-card flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-base font-medium transition-colors sm:min-h-0 sm:text-sm",
          isActive
            ? "text-neutral-black bg-violet-50"
            : "text-neutral-grey-dark hover:bg-violet-75 active:bg-violet-50",
        )}
        onClick={onClick}>
        <ItemIcon className={clsxm("size-4 shrink-0", isActive ? "text-violet-500" : "text-neutral-grey-dark")} />
        <span className="truncate">{label}</span>
        {!!indicator && (
          <>
            <span
              aria-hidden
              className={clsxm(
                "size-2 shrink-0 rounded-full",
                indicator.tone === "danger" ? "bg-danger-rubicund" : "bg-warning-mustard",
              )}
            />
            <span className="sr-only">{indicator.label}</span>
          </>
        )}
        <RightChevronIcon className="text-neutral-grey-light ml-auto size-2.5 shrink-0 sm:hidden" />
      </button>
    );
  };

  const renderAdminSections = ({className, scope: rowScope}: {className?: string; scope: TSettingsScope}) =>
    isAdmin && (
      <div className="bg-primary-light-solid flex flex-col gap-1 rounded-lg py-2">
        <span className="text-neutral-grey-dark text-2xs px-3 font-semibold tracking-wider uppercase">
          {t("common:admin")}
        </span>
        <div className={clsxm("flex flex-col gap-2 px-2 sm:gap-0.5", className)}>
          {SETTINGS_ADMIN_SECTIONS.map(itemSection =>
            renderSectionItem({
              isActive: section === itemSection && scope.id === rowScope.id && scope.type === rowScope.type,
              itemSection,
              onClick: () => onSelect({scope: rowScope, section: itemSection}),
            }),
          )}
        </div>
      </div>
    );

  const renderScopeSections = ({missingProfileFields, scope: rowScope, sections}: TSettingsScopeRow) =>
    sections.map(itemSection =>
      renderSectionItem({
        indicator:
          itemSection === "billing" && getPaymentFailedInvoices({scope: rowScope}).length > 0
            ? {label: t("common:payment_failed"), tone: "danger"}
            : itemSection === "info" && missingProfileFields.length > 0
              ? {label: missingProfileFields.map(key => t(key as "common:payment_failed")).join(", "), tone: "warning"}
              : itemSection === "users" &&
                  rowScope.id === scope.id &&
                  rowScope.type === scope.type &&
                  hasPendingPermissionRequest
                ? {label: t("multi_account:permission_request"), tone: "warning"}
                : undefined,
        isActive: section === itemSection && scope.id === rowScope.id && scope.type === rowScope.type,
        itemSection,
        onClick: () => onSelect({scope: rowScope, section: itemSection}),
      }),
    );

  const renderDashboardLink = (rowScope: TSettingsScope) => (
    <Link
      aria-label={t("settings:dashboard_open")}
      className="text-neutral-grey-dark hover:text-primary-dark-solid flex shrink-0 items-center justify-center pl-3 transition-colors max-sm:min-h-12"
      params={{agencyId: agency?.id!, companyId: rowScope.id}}
      target="_blank"
      title={t("settings:dashboard_open")}
      to={
        rowScope.type === "agency"
          ? "/agency/$agencyId/dashboard"
          : agency?.id
            ? "/agency/$agencyId/subbrand/$companyId/dashboard"
            : "/company/$companyId/dashboard"
      }>
      <ExternalLink className="size-4" />
    </Link>
  );

  const renderScopeRow = (row: TSettingsScopeRow) => {
    const {scope: rowScope, name, picture} = row;
    const key = getScopeKey(rowScope);
    const isExpanded = expandedKey === key;

    return (
      <div
        className="flex flex-col gap-2 sm:gap-0.5"
        data-active-scope={key === scopeKey ? "true" : undefined}
        key={key}>
        <div
          className={clsxm(
            "flex items-center rounded-lg pr-3 transition-colors",
            isExpanded ? "bg-violet-50" : "hover:bg-violet-75 has-[button:active]:bg-violet-50",
          )}>
          <button
            className="text-neutral-black flex min-h-12 min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-base font-semibold sm:min-h-0 sm:text-sm"
            onClick={() => setExpandedKey(isExpanded ? null : key)}>
            <ScopeAvatar name={name} picture={picture} />
            <span className="flex-1 truncate text-left">{name}</span>
            <RightChevronIcon
              className={clsxm(
                "text-neutral-grey-dark size-2.5 shrink-0 transition-transform",
                isExpanded ? "-rotate-90" : "rotate-90",
              )}
            />
          </button>
          {renderDashboardLink(rowScope)}
        </div>
        <AnimatedAccordion condition={isExpanded} className="overflow-hidden">
          <div className="flex flex-col gap-1">
            <div className="border-neutral-grey-light-dark ml-4 flex flex-col gap-2 border-l pl-2 sm:gap-0.5">
              {renderScopeSections(row)}
            </div>
            {renderAdminSections({className: "border-neutral-grey-light-dark ml-4 border-l", scope: rowScope})}
          </div>
        </AnimatedAccordion>
      </div>
    );
  };

  return (
    <nav
      ref={navRef}
      className="bg-violet-25 border-neutral-grey-light-dark flex min-h-0 w-full flex-col gap-3 overflow-y-auto p-3 max-sm:grow sm:w-62 sm:shrink-0 sm:border-r">
      <div className="flex flex-col gap-1">
        <span className="text-neutral-grey-dark text-2xs px-3 font-semibold tracking-wider uppercase">
          {t("common:account")}
        </span>
        <div className="flex flex-col gap-2 sm:gap-0.5">
          {SETTINGS_ACCOUNT_SECTIONS.map(itemSection =>
            renderSectionItem({
              isActive: section === itemSection,
              itemSection,
              onClick: () => onSelect({section: itemSection}),
            }),
          )}
        </div>
      </div>
      {flatRow ? (
        <>
          <div className="border-neutral-grey-light-dark border-t" />
          <div className="flex flex-col gap-1">
            <div className="flex items-center pr-3">
              <span className="text-neutral-grey-dark text-2xs flex-1 truncate px-3 font-semibold tracking-wider uppercase">
                {flatRow.name}
              </span>
              {renderDashboardLink(flatRow.scope)}
            </div>
            <div className="flex flex-col gap-2 sm:gap-0.5">{renderScopeSections(flatRow)}</div>
            {renderAdminSections({scope: flatRow.scope})}
          </div>
        </>
      ) : (
        <>
          {agencyRows.length > 0 && (
            <>
              <div className="border-neutral-grey-light-dark border-t" />
              <div className="flex flex-col gap-1">
                <span className="text-neutral-grey-dark text-2xs px-3 font-semibold tracking-wider uppercase">
                  {t(agency?.large_brand ? "multi_account:large_brand" : "settings:agency")}
                </span>
                <div className="flex flex-col gap-2 sm:gap-0.5">{agencyRows.map(renderScopeRow)}</div>
              </div>
            </>
          )}
          {brandRows.length > 0 && (
            <>
              <div className="border-neutral-grey-light-dark border-t" />
              <div className="flex flex-col gap-1">
                <span className="text-neutral-grey-dark text-2xs px-3 font-semibold tracking-wider uppercase">
                  {t("settings:clients")}
                </span>
                {brandRows.length > SEARCH_THRESHOLD && (
                  <div className="group">
                    <InputField
                      className="border-neutral-grey-light-dark h-9 rounded-lg bg-white pr-8 pl-9 text-xs"
                      icon={
                        <SearchIcon className="text-neutral-grey-light group-focus-within:text-primary-dark-solid absolute top-1/2 left-3 size-4 -translate-y-1/2 transition-colors" />
                      }
                      onChange={setBrandQuery}
                      otherIcon={
                        brandQuery ? (
                          <button
                            aria-label={t("common:clear_search")}
                            className="absolute top-1/2 right-2 -translate-y-1/2"
                            onClick={() => setBrandQuery("")}
                            type="button">
                            <TimesIcon className="text-primary-dark-solid border-primary-light-solid size-5 rounded-full border p-1" />
                          </button>
                        ) : null
                      }
                      placeholder={t("settings:search_clients")}
                      value={brandQuery}
                    />
                  </div>
                )}
                {agencyRows.length > 0 && (
                  <button
                    className="text-primary-dark-solid hover:text-primary-medium-solid disabled:text-neutral-grey-light flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed"
                    disabled={seatsLeft <= 0}
                    onClick={() => onSelect({scope, section, subview: {type: "create_brand"}})}>
                    <PlusIcon className="size-4 shrink-0" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">
                        {t(agency?.large_brand ? "multi_account:add_company" : "settings:add_brand")}
                      </span>
                      {seatsLeft <= INFINITY_THRESHOLD && (
                        <span className="text-neutral-grey-dark text-xs font-normal">
                          {t("multi_account:seats_left", {count: seatsLeft})}
                        </span>
                      )}
                    </div>
                  </button>
                )}
                <div className="flex flex-col gap-2 sm:gap-0.5">
                  {visibleBrandRows.length > 0 ? (
                    visibleBrandRows.map(renderScopeRow)
                  ) : (
                    <span className="text-neutral-grey-dark px-3 py-2 text-sm">
                      {t("common:brand_search_no_results")}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </nav>
  );
};

export default SettingsRail;
